import { useEffect, useState } from 'react';
import { api, type ExportPaket } from '../api/client';
import { Meldung, fehlerText } from '../components/ui';
import { uhrzeit } from '../format';
import { PostenkarteInhalt } from './Druck';

const SPEICHER = 'umzugsplaner.postenkarte.';

/**
 * Skizze 4: Postenkarte mobil. Wird beim ersten Öffnen lokal gespeichert und ist danach offline lesbar.
 * „Posten besetzt melden“ vermerkt den Zeitpunkt nur auf diesem Gerät – es gibt (bewusst) keine Übermittlung.
 */
export function Postenkarte({ planversion, postenId }: { planversion: string; postenId: string }) {
  const [paket, setPaket] = useState<ExportPaket | null>(null);
  const [offline, setOffline] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [besetzt, setBesetzt] = useState<string | null>(() =>
    lesen(`${SPEICHER}besetzt.${postenId}`),
  );

  useEffect(() => {
    api
      .export(planversion)
      .then((p) => {
        setPaket(p);
        schreiben(`${SPEICHER}${planversion}`, JSON.stringify(p));
      })
      .catch((e: unknown) => {
        const gespeichert = lesen(`${SPEICHER}${planversion}`);
        if (gespeichert) {
          setPaket(JSON.parse(gespeichert) as ExportPaket);
          setOffline(true);
        } else {
          setFehler(fehlerText(e));
        }
      });
  }, [planversion]);

  const po = paket?.posten.find((x) => x.id === postenId);

  return (
    <div className="mobil">
      <header className="mobil__kopf">
        <strong>Postenkarte</strong>
        <span>{paket?.veranstaltung.titel}</span>
      </header>
      {offline && (
        <Meldung art="info">
          Offline – gespeicherter Stand vom {new Date(paket!.erstellt).toLocaleString('de-DE')}.
        </Meldung>
      )}
      {fehler && (
        <Meldung art="fehler">
          {fehler} (Postenkarte einmal mit Netz und angemeldet öffnen, dann ist sie offline
          verfügbar.)
        </Meldung>
      )}
      {paket && !po && <Meldung art="fehler">Dieser Posten ist nicht mehr im Plan.</Meldung>}
      {paket && po && <PostenkarteInhalt p={paket} po={po} />}
      {po && (
        <div className="mobil__aktion">
          {besetzt ? (
            <p className="besetzt">
              ✓ Als besetzt vermerkt um {uhrzeit(besetzt)} Uhr
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  entfernen(`${SPEICHER}besetzt.${postenId}`);
                  setBesetzt(null);
                }}
              >
                Zurücknehmen
              </button>
            </p>
          ) : (
            <button
              type="button"
              className="knopf knopf--primaer knopf--gross"
              onClick={() => {
                const t = new Date().toISOString();
                schreiben(`${SPEICHER}besetzt.${postenId}`, t);
                setBesetzt(t);
              }}
            >
              Posten besetzt melden
            </button>
          )}
          <p className="gedaempft">
            Wird nur auf diesem Gerät vermerkt – Meldung an die Einsatzleitung bitte über Funk.
          </p>
        </div>
      )}
      <footer className="mobil__fuss">
        {paket?.hinweis} Kartendaten {paket?.attribution}.
      </footer>
    </div>
  );
}

function lesen(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function schreiben(k: string, v: string): void {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* Speicher voll oder gesperrt – Karte funktioniert trotzdem online */
  }
}
function entfernen(k: string): void {
  try {
    window.localStorage.removeItem(k);
  } catch {
    /* ignorieren */
  }
}
